import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { WorkspaceContext, type WorkspaceContextValue } from "./workspace-context";
import type { Classification, PersonalWorkspaceRuntime, WorkspaceCommand, WorkspaceSnapshot } from "./workspace-api";

type Runtime = PersonalWorkspaceRuntime | null;
interface Stored { runtime: Runtime; snapshot: WorkspaceSnapshot | null; fresh: boolean; busy: boolean; error: string; last: WorkspaceCommand | null; pending: WorkspaceCommand | null }
const matches = (a: Runtime, b: Runtime) => a?.userId === b?.userId && a?.api === b?.api;
const empty = (runtime: Runtime): Stored => ({ runtime, snapshot: null, fresh: false, busy: false, error: "", last: null, pending: null });
function requireOwnSnapshot(value: WorkspaceSnapshot, userId: string) {
  if (value.actor !== userId || !Number.isSafeInteger(value.revision) || value.revision < 0
    || value.intervals.some(row => row.user_id !== userId)
    || value.intervals.filter(row => row.stopped_at === null).length > 1
    || value.intervals.some(row => !Number.isFinite(Date.parse(row.started_at)) || (row.stopped_at !== null && !Number.isFinite(Date.parse(row.stopped_at))))) {
    throw new Error("Time is unavailable for this account.");
  }
  return value;
}

/** No runtime is supplied by the application. Injection is for local qualification only. */
export function PersonalWorkspaceProvider({ children, runtime: supplied }: { children: ReactNode; runtime?: PersonalWorkspaceRuntime }) {
  const runtime = supplied ?? null;
  const [stored, setStored] = useState<Stored>(() => empty(runtime));
  const identity = useRef<Runtime>(null);
  const generation = useRef({ value: 0 });
  const sessionEpoch = useRef({ value: 0 });
  const fresh = useRef(false);
  const inFlight = useRef(false);
  const state = matches(stored.runtime, runtime) ? stored : empty(runtime);
  const userId = runtime?.userId;
  const api = runtime?.api;
  const refresh = useCallback(async (reconciling = false) => {
    if (!userId || !api || (inFlight.current && !reconciling)) return false;
    const requestRuntime = { userId, api };
    const request = ++generation.current.value;
    try {
      const snapshot = requireOwnSnapshot(await api.read(), userId);
      if (!matches(identity.current, requestRuntime) || generation.current.value !== request) return false;
      fresh.current = true;
      setStored(old => ({ ...old, runtime: requestRuntime, snapshot, fresh: true, error: "" }));
      return true;
    } catch (e) {
      if (!matches(identity.current, requestRuntime) || generation.current.value !== request) return false;
      fresh.current = false;
      setStored(old => ({ ...old, runtime: requestRuntime, fresh: false, error: (e as Error).message }));
      return false;
    }
  }, [userId, api]);
  useEffect(() => {
    const requests = generation.current;
    const epochs = sessionEpoch.current;
    epochs.value++;
    identity.current = userId && api ? { userId, api } : null;
    generation.current.value++; fresh.current = false; inFlight.current = false;
    setStored(empty(identity.current));
    if (!identity.current) return;
    void refresh();
    const poll = setInterval(() => void refresh(), 10000);
    return () => { clearInterval(poll); identity.current = null; epochs.value++; requests.value++; fresh.current = false; };
  }, [userId, api, refresh]);

  async function execute(command: WorkspaceCommand): Promise<boolean> {
    if (!runtime || !matches(identity.current, runtime) || !fresh.current || inFlight.current) return false;
    const commandRuntime = runtime;
    const commandEpoch = sessionEpoch.current.value;
    const currentCommandSession = () => matches(identity.current, commandRuntime) && sessionEpoch.current.value === commandEpoch;
    inFlight.current = true; generation.current.value++;
    setStored(old => ({ ...old, busy: true, pending: null, last: command, error: "" }));
    try {
      const snapshot = requireOwnSnapshot(await runtime.api.execute(command), runtime.userId);
      if (!currentCommandSession()) return false;
      fresh.current = true;
      setStored(old => ({ ...old, snapshot, fresh: true }));
      return true;
    } catch (e) {
      if (!currentCommandSession()) return false;
      fresh.current = false;
      setStored(old => ({ ...old, fresh: false }));
      const reconciled = await refresh(true);
      if (currentCommandSession()) setStored(old => ({ ...old, error: `${(e as Error).message} No command was automatically retried.${reconciled ? "" : " Refresh time before making changes."}` }));
      return false;
    } finally {
      if (currentCommandSession()) { inFlight.current = false; setStored(old => ({ ...old, busy: false })); }
    }
  }
  const active = state.snapshot?.intervals.find(row => row.stopped_at === null);
  const writable = (org: string, client: string) => state.snapshot?.clients.some(c => c.id === client && c.organization_id === org && c.writable);
  const available = () => Boolean(runtime && matches(identity.current, runtime) && fresh.current && !inFlight.current && state.snapshot && !state.snapshot.hidden_running);
  function target(clientId: string, classification: Classification, kind: "start" | "switch"): WorkspaceCommand | null {
    const client = state.snapshot?.clients.find(c => c.id === clientId && c.writable);
    if (!available() || !client || (kind === "start" && active) || (kind === "switch" && (!active || !writable(active.organization_id, active.client_company_id)))) return null;
    return { type: "timer", key: crypto.randomUUID(), revision: state.snapshot!.revision, kind, current: active?.id ?? null, new: crypto.randomUUID(), organization_id: client.organization_id, client_id: client.id, classification };
  }
  function mayReplay(command: WorkspaceCommand | null) {
    if (!command || !available()) return false;
    if (command.type === "correction") {
      const row = state.snapshot?.intervals.find(r => r.id === command.interval_id && r.stopped_at !== null);
      return Boolean(row && writable(row.organization_id, row.client_company_id));
    }
    if (command.kind !== "start") {
      const source = state.snapshot?.intervals.find(r => r.id === command.current);
      if (!source || !writable(source.organization_id, source.client_company_id)) return false;
    }
    return command.kind === "stop" || Boolean(command.organization_id && command.client_id && writable(command.organization_id, command.client_id));
  }
  const value: WorkspaceContextValue | null = runtime ? {
    session: runtime.api, userId: runtime.userId, snapshot: state.snapshot, fresh: state.fresh, busy: state.busy, error: state.error, last: state.last, pending: state.pending, canRetry: mayReplay(state.last),
    refresh: () => refresh(),
    start: (client, classification) => { const command = target(client, classification, "start"); if (command) void execute(command); },
    reviewSwitch: (client, classification) => { const command = target(client, classification, "switch"); if (command) setStored(old => ({ ...old, pending: command })); },
    confirmSwitch: () => {
      const pending = state.pending;
      if (!available() || pending?.type !== "timer" || pending.kind !== "switch" || pending.current !== active?.id
        || pending.revision !== state.snapshot?.revision || !active || !writable(active.organization_id, active.client_company_id)
        || !writable(pending.organization_id!, pending.client_id!)) {
        setStored(old => ({ ...old, pending: null, error: "Stored time changed. Review the switch again." })); return;
      }
      void execute(pending);
    },
    cancelSwitch: () => setStored(old => ({ ...old, pending: null })),
    stop: () => { if (available() && active && writable(active.organization_id, active.client_company_id)) void execute({ type: "timer", key: crypto.randomUUID(), revision: state.snapshot!.revision, kind: "stop", current: active.id }); },
    retry: () => { if (state.last && mayReplay(state.last)) void execute(state.last); },
    correct: async (id, revision, started_at, stopped_at, reason) => {
      const row = state.snapshot?.intervals.find(r => r.id === id && r.stopped_at !== null);
      const zoned = (time: string) => /(Z|[+-]\d{2}:\d{2})$/.test(time) && Number.isFinite(Date.parse(time));
      if (!available() || !row || revision !== state.snapshot?.revision || !writable(row.organization_id, row.client_company_id)
        || !reason.trim() || reason.length > 2000 || !zoned(started_at) || !zoned(stopped_at) || Date.parse(stopped_at) <= Date.parse(started_at)) return false;
      return execute({ type: "correction", key: crypto.randomUUID(), revision, interval_id: id, event_id: crypto.randomUUID(), started_at, stopped_at, reason: reason.trim() });
    },
  } : null;
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
